import { useEffect, useState } from 'react';
import { Skeleton } from '../../components/Skeleton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router';
import type { UnitKey } from '@fewerbags/schema';
import { Alert } from '../../components/Alert';
import { AppShell } from '../../components/AppShell';
import { Button } from '../../components/Button';
import { Field } from '../../components/Field';
import { Section } from '../../components/Section';
import { ConfidenceBadge } from '../../components/recipes/ConfidenceBadge';
import { RowControls } from '../../components/recipes/RowControls';
import { TagChipInput } from '../../components/recipes/TagChipInput';
import { UNIT_OPTIONS } from '../../lib/units';
import { api } from '../../lib/api';
import { messageForError } from '../../lib/errors';
import { moveItem } from '../recipes/recipe-form';
import { importKeys, recipeKeys } from '../recipes/queries';
import {
  approvalBlockers,
  draftToState,
  stateToDraft,
  type DraftIngredientRow,
  type DraftState,
  type DraftStepRow,
} from './import-draft';

export function ImportReviewPage() {
  const { jobId = '' } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<DraftState | null>(null);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const job = useQuery({
    queryKey: importKeys.detail(jobId),
    queryFn: () => api.imports.get(jobId),
    enabled: jobId !== '',
  });

  const draft = job.data?.job.draft ?? null;
  useEffect(() => {
    if (!draft || loadedId === jobId) return;
    setState(draftToState(draft));
    setLoadedId(jobId);
  }, [draft, jobId, loadedId]);

  const save = useMutation({
    mutationFn: () => api.imports.update(jobId, { draft: stateToDraft(state!) }),
    onSuccess: (res) => {
      qc.setQueryData(importKeys.detail(jobId), res);
      void qc.invalidateQueries({ queryKey: importKeys.list });
      setSaved(true);
    },
  });
  const approve = useMutation({
    mutationFn: async () => {
      if (state) await api.imports.update(jobId, { draft: stateToDraft(state) });
      return api.imports.approve(jobId);
    },
    onSuccess: async (res) => {
      await qc.invalidateQueries({ queryKey: importKeys.all });
      await qc.invalidateQueries({ queryKey: recipeKeys.all });
      void navigate(`/recipes/${res.recipeId}`, { replace: true });
    },
  });
  const reject = useMutation({
    mutationFn: () => api.imports.reject(jobId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: importKeys.all });
      void navigate('/recipes/import', { replace: true });
    },
  });

  if (job.isPending) {
    return (
      <AppShell title="Review import">
        <Skeleton lines={4} />
      </AppShell>
    );
  }
  if (job.isError || !job.data) {
    return (
      <AppShell title="Review import">
        <Alert tone="error">{messageForError(job.error)}</Alert>
        <Link to="/recipes/import" className="mt-3 inline-block text-sm text-brand underline">
          Back to imports
        </Link>
      </AppShell>
    );
  }

  const j = job.data.job;

  if (!draft || !state) {
    return (
      <AppShell title="Review import">
        <Alert tone="error">
          {j.error ?? 'That page could not be read as a recipe. Try pasting the text instead.'}
        </Alert>
        <Link to="/recipes/import" className="mt-3 inline-block text-sm text-brand underline">
          Back to imports
        </Link>
      </AppShell>
    );
  }

  if (j.reviewState !== 'pending') {
    return (
      <AppShell title="Review import">
        <Alert tone="info">
          This import was already {j.reviewState}.{' '}
          {j.createdRecipeId ? (
            <Link to={`/recipes/${j.createdRecipeId}`} className="text-brand underline">
              Open the recipe
            </Link>
          ) : null}
        </Alert>
        <Link to="/recipes/import" className="mt-3 inline-block text-sm text-brand underline">
          Back to imports
        </Link>
      </AppShell>
    );
  }

  const blockers = approvalBlockers(state);
  const patchIngredient = (i: number, next: Partial<DraftIngredientRow>) =>
    setState({
      ...state,
      ingredients: state.ingredients.map((row, j2) => (j2 === i ? { ...row, ...next } : row)),
    });
  const patchStep = (i: number, next: Partial<DraftStepRow>) =>
    setState({
      ...state,
      steps: state.steps.map((row, j2) => (j2 === i ? { ...row, ...next } : row)),
    });
  const dirty = () => setSaved(false);

  return (
    <AppShell title="Review import">
      <div className="flex flex-col gap-4">
        {state.reviewNotes.length > 0 ? (
          <Alert tone="info">
            <ul className="list-disc pl-5">
              {state.reviewNotes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          </Alert>
        ) : null}

        <Section
          accent="orange"
          title="Recipe"
          description="Nothing is saved as a recipe until you approve it."
        >
          <Field
            id="draft-title"
            label="Title"
            value={state.title}
            maxLength={200}
            onChange={(e) => {
              dirty();
              setState({ ...state, title: e.target.value });
            }}
          />
          <div className="flex flex-col gap-1">
            <label htmlFor="draft-description" className="text-sm font-medium text-ink">
              Description
            </label>
            <textarea
              id="draft-description"
              rows={3}
              maxLength={2000}
              value={state.description}
              onChange={(e) => {
                dirty();
                setState({ ...state, description: e.target.value });
              }}
              className="rounded-lg border border-line bg-surface p-3 text-base text-ink"
            />
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Field
              id="draft-servings"
              label="Serves"
              type="number"
              inputMode="numeric"
              min={1}
              max={200}
              value={state.servings}
              onChange={(e) => {
                dirty();
                setState({ ...state, servings: e.target.value });
              }}
            />
            <Field
              id="draft-prep"
              label="Prep (min)"
              type="number"
              inputMode="numeric"
              min={0}
              value={state.prepMin}
              onChange={(e) => {
                dirty();
                setState({ ...state, prepMin: e.target.value });
              }}
            />
            <Field
              id="draft-cook"
              label="Cook (min)"
              type="number"
              inputMode="numeric"
              min={0}
              value={state.cookMin}
              onChange={(e) => {
                dirty();
                setState({ ...state, cookMin: e.target.value });
              }}
            />
            <Field
              id="draft-total"
              label="Total (min)"
              type="number"
              inputMode="numeric"
              min={0}
              value={state.totalMin}
              onChange={(e) => {
                dirty();
                setState({ ...state, totalMin: e.target.value });
              }}
            />
          </div>
          <TagChipInput
            value={state.tags}
            onChange={(tags) => {
              dirty();
              setState({ ...state, tags });
            }}
          />
          {state.imageUrl ? (
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-ink">Photo on the source page</span>
              <img
                src={state.imageUrl}
                alt=""
                referrerPolicy="no-referrer"
                className="max-h-40 w-full rounded-lg border border-line object-cover"
              />
              <p className="text-xs text-muted">
                Shown for reference only — the image stays on the original site and is not copied
                into FewerBags.
              </p>
            </div>
          ) : null}
          <p className="text-xs text-muted">
            Source: {state.source.siteName ?? state.source.ref} · {state.source.licence}
          </p>
        </Section>

        <Section
          accent="orange"
          title="Ingredients"
          description="Each line shows how sure the parser was. Fix anything flagged."
        >
          <ul className="flex flex-col gap-3">
            {state.ingredients.map((row, i) => (
              <li key={row.key} className="rounded-lg border border-line p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <ConfidenceBadge confidence={row.confidence} />
                  <span className="min-w-0 flex-1 truncate text-xs text-muted" title={row.raw}>
                    {row.raw}
                  </span>
                  <RowControls
                    label={`ingredient ${i + 1}`}
                    index={i}
                    total={state.ingredients.length}
                    onMove={(to) => {
                      dirty();
                      setState({ ...state, ingredients: moveItem(state.ingredients, i, to) });
                    }}
                    onRemove={() => {
                      dirty();
                      setState({
                        ...state,
                        ingredients: state.ingredients.filter((_, j2) => j2 !== i),
                      });
                    }}
                  />
                </div>
                <div className="mt-2 flex flex-wrap items-end gap-2">
                  <div className="flex w-24 flex-col gap-1">
                    <label htmlFor={`dq-${row.key}`} className="text-xs font-medium text-muted">
                      Amount
                    </label>
                    <input
                      id={`dq-${row.key}`}
                      value={row.qtyText}
                      onChange={(e) => {
                        dirty();
                        patchIngredient(i, { qtyText: e.target.value });
                      }}
                      className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                    />
                  </div>
                  <div className="flex w-32 flex-col gap-1">
                    <label htmlFor={`du-${row.key}`} className="text-xs font-medium text-muted">
                      Unit
                    </label>
                    <select
                      id={`du-${row.key}`}
                      value={row.unit}
                      onChange={(e) => {
                        dirty();
                        patchIngredient(i, { unit: e.target.value as UnitKey });
                      }}
                      className="min-h-11 rounded-lg border border-line bg-surface px-2 text-base text-ink"
                    >
                      {UNIT_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex min-w-40 flex-1 flex-col gap-1">
                    <label htmlFor={`dn-${row.key}`} className="text-xs font-medium text-muted">
                      Ingredient
                    </label>
                    <input
                      id={`dn-${row.key}`}
                      value={row.ingredient}
                      onChange={(e) => {
                        dirty();
                        patchIngredient(i, { ingredient: e.target.value });
                      }}
                      className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                    />
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <div className="flex min-w-40 flex-1 flex-col gap-1">
                    <label htmlFor={`dp-${row.key}`} className="sr-only">
                      Preparation note for ingredient {i + 1}
                    </label>
                    <input
                      id={`dp-${row.key}`}
                      value={row.prep}
                      placeholder="prep note"
                      onChange={(e) => {
                        dirty();
                        patchIngredient(i, { prep: e.target.value });
                      }}
                      className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink placeholder:text-muted"
                    />
                  </div>
                  <label className="flex min-h-11 items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={row.optional}
                      onChange={(e) => {
                        dirty();
                        patchIngredient(i, { optional: e.target.checked });
                      }}
                      className="size-5 accent-[var(--color-brand)]"
                    />
                    Optional
                  </label>
                </div>
                {row.notes.length > 0 ? (
                  <ul className="mt-2 list-disc pl-5 text-xs text-muted">
                    {row.notes.map((note) => (
                      <li key={note}>{note}</li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </Section>

        <Section accent="orange" title="Steps">
          <ol className="flex flex-col gap-3">
            {state.steps.map((row, i) => (
              <li key={row.key} className="rounded-lg border border-line p-3">
                <div className="flex items-start gap-2">
                  <span
                    aria-hidden
                    className="mt-1 flex size-7 shrink-0 items-center justify-center rounded-full bg-brand-soft text-sm font-semibold text-brand"
                  >
                    {i + 1}
                  </span>
                  <div className="flex flex-1 flex-col gap-1">
                    <label htmlFor={`ds-${row.key}`} className="sr-only">
                      Step {i + 1}
                    </label>
                    <textarea
                      id={`ds-${row.key}`}
                      rows={2}
                      value={row.text}
                      onChange={(e) => {
                        dirty();
                        patchStep(i, { text: e.target.value });
                      }}
                      className="rounded-lg border border-line bg-surface p-3 text-base text-ink"
                    />
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap items-center gap-3">
                  <div className="flex w-28 flex-col gap-1">
                    <label htmlFor={`dd-${row.key}`} className="text-xs font-medium text-muted">
                      Minutes
                    </label>
                    <input
                      id={`dd-${row.key}`}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      value={row.durationText}
                      onChange={(e) => {
                        dirty();
                        patchStep(i, { durationText: e.target.value });
                      }}
                      className="min-h-11 rounded-lg border border-line bg-surface px-3 text-base text-ink"
                    />
                  </div>
                  <label className="flex min-h-11 items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={row.attended}
                      onChange={(e) => {
                        dirty();
                        patchStep(i, { attended: e.target.checked });
                      }}
                      className="size-5 accent-[var(--color-brand)]"
                    />
                    Hands-on
                  </label>
                  <label className="flex min-h-11 items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={row.timer}
                      onChange={(e) => {
                        dirty();
                        patchStep(i, { timer: e.target.checked });
                      }}
                      className="size-5 accent-[var(--color-brand)]"
                    />
                    Timer
                  </label>
                  <label className="flex min-h-11 items-center gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={row.canDoAhead}
                      onChange={(e) => {
                        dirty();
                        patchStep(i, { canDoAhead: e.target.checked });
                      }}
                      className="size-5 accent-[var(--color-brand)]"
                    />
                    Can do ahead
                  </label>
                  <RowControls
                    label={`step ${i + 1}`}
                    index={i}
                    total={state.steps.length}
                    onMove={(to) => {
                      dirty();
                      setState({ ...state, steps: moveItem(state.steps, i, to) });
                    }}
                    onRemove={() => {
                      dirty();
                      setState({ ...state, steps: state.steps.filter((_, j2) => j2 !== i) });
                    }}
                  />
                </div>
              </li>
            ))}
          </ol>
        </Section>

        {blockers.length > 0 ? (
          <Alert tone="warning">
            <ul className="list-disc pl-5">
              {blockers.map((b) => (
                <li key={b}>{b}</li>
              ))}
            </ul>
          </Alert>
        ) : null}
        {save.isError ? <Alert tone="error">{messageForError(save.error)}</Alert> : null}
        {approve.isError ? <Alert tone="error">{messageForError(approve.error)}</Alert> : null}
        {reject.isError ? <Alert tone="error">{messageForError(reject.error)}</Alert> : null}
        {saved ? <Alert tone="success">Draft saved.</Alert> : null}

        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            onClick={() => approve.mutate()}
            disabled={blockers.length > 0 || approve.isPending}
          >
            {approve.isPending ? 'Creating the recipe…' : 'Approve and save'}
          </Button>
          <Button
            variant="secondary"
            onClick={() => save.mutate()}
            disabled={save.isPending || approve.isPending}
          >
            Save draft for later
          </Button>
          <Button variant="danger" onClick={() => reject.mutate()} disabled={reject.isPending}>
            Reject
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
